import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Plus, Mail, Pencil, Check, X, Ban, CheckCircle2, ClipboardList } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useTenantScope } from '../context/TenantScopeContext';
import { ColumnDef, DraggableTh, useColumnPrefs } from '../components/ColumnPicker';

interface Tenant {
  id: number;
  partner_id: number;
  name: string;
  slug: string;
  contact_email: string | null;
  alert_only_mode: boolean;
  active: boolean;
  onboarding_completed_at: string | null;
  // Dati anagrafici per il frontespizio del report (2026-10-20).
  company_info: CompanyInfo;
}

interface CompanyInfo { legal_name?: string; address?: string; vat_number?: string; contact_name?: string; contact_phone?: string }

const COMPANY_FIELDS: { key: keyof CompanyInfo; label: string }[] = [
  { key: 'legal_name', label: 'Legal name' },
  { key: 'address', label: 'Address' },
  { key: 'vat_number', label: 'VAT number' },
  { key: 'contact_name', label: 'Contact person' },
  { key: 'contact_phone', label: 'Phone' },
];

interface Partner {
  id: number;
  name: string;
}

const TENANT_COLUMNS: ColumnDef[] = [
  { key: 'name', label: 'Name', defaultVisible: true },
  { key: 'slug', label: 'Slug', defaultVisible: true },
  { key: 'contact_email', label: 'Contact email', defaultVisible: true },
  { key: 'mode', label: 'Mode', defaultVisible: true },
  { key: 'status', label: 'Status', defaultVisible: true },
  { key: 'actions', label: 'Actions', defaultVisible: true },
];

export default function TenantsPage() {
  const { user, isAdmin } = useAuth();
  const { setSelectedTenantId, reloadTenants } = useTenantScope();
  const navigate = useNavigate();
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [partners, setPartners] = useState<Partner[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [partnerId, setPartnerId] = useState<string>(user?.partnerId ? String(user.partnerId) : '');
  const [error, setError] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editName, setEditName] = useState('');
  const [editEmail, setEditEmail] = useState('');
  const [editAlertOnly, setEditAlertOnly] = useState(false);
  const [editCompany, setEditCompany] = useState<CompanyInfo>({});
  const { orderedDefs, moveColumn } = useColumnPrefs('soc.columns.tenants', TENANT_COLUMNS);

  async function load() {
    setLoading(true);
    const requests = [axios.get('/api/tenants')];
    if (user?.scopeLevel === 'platform') requests.push(axios.get('/api/partners'));
    const [tenantsRes, partnersRes] = await Promise.all(requests);
    setTenants(tenantsRes.data.data);
    if (partnersRes) setPartners(partnersRes.data.data);
    setLoading(false);
  }

  useEffect(() => { load(); }, []);

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      const res = await axios.post('/api/tenants', { partnerId: Number(partnerId), name, slug, contactEmail: contactEmail || null });
      setName(''); setSlug(''); setContactEmail(''); setShowForm(false);
      // Appena creato si apre il wizard di onboarding (asset critici, notifiche, accesso remoto,
      // fascia notturna): il tenant entra anche nel menu "Viewing" senza rifare il login.
      await reloadTenants();
      setSelectedTenantId(res.data.data.id);
      navigate('/onboarding');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Error while creating the tenant');
    }
  }

  function startEdit(t: Tenant) {
    setEditingId(t.id);
    setEditName(t.name);
    setEditEmail(t.contact_email || '');
    setEditAlertOnly(t.alert_only_mode);
    setEditCompany({ ...(t.company_info || {}) });
  }

  async function saveEdit(tenantId: number) {
    await axios.put(`/api/tenants/${tenantId}`, {
      name: editName, contactEmail: editEmail || null, alertOnlyMode: editAlertOnly, companyInfo: editCompany,
    });
    setEditingId(null);
    load();
  }

  async function toggleActive(t: Tenant) {
    await axios.put(`/api/tenants/${t.id}`, { active: !t.active });
    load();
  }

  function renderCell(t: Tenant, key: string) {
    const editing = editingId === t.id;
    switch (key) {
      case 'name':
        return editing ? (
          <input value={editName} onChange={e => setEditName(e.target.value)} className="input-field py-1 text-xs w-40" />
        ) : (
          <button onClick={() => { setSelectedTenantId(t.id); navigate('/overview'); }} className="hover:underline font-medium text-primary-700">
            {t.name}
          </button>
        );
      case 'slug':
        return <span className="text-gray-500">{t.slug}</span>;
      case 'contact_email':
        return editing ? (
          <input type="email" value={editEmail} onChange={e => setEditEmail(e.target.value)}
            placeholder="security@customer.com" className="input-field py-1 text-xs w-56" />
        ) : (
          <span className="flex items-center gap-1.5 text-gray-600">
            <Mail size={14} className={t.contact_email ? 'text-primary-500' : 'text-gray-300'} />
            <span className={t.contact_email ? '' : 'italic text-gray-400'}>
              {t.contact_email || 'Not set'}
            </span>
          </span>
        );
      case 'mode':
        return editing ? (
          <label className="inline-flex items-center gap-1.5 text-xs text-gray-600">
            <input type="checkbox" checked={editAlertOnly} onChange={e => setEditAlertOnly(e.target.checked)} />
            Alert-only
          </label>
        ) : <span className="text-gray-500">{t.alert_only_mode ? 'Alert-only' : 'Automated response'}</span>;
      case 'status':
        return (
          <span className="inline-flex items-center gap-1">
            <span className={`badge ${t.active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
              {t.active ? 'Active' : 'Inactive'}
            </span>
            {!t.onboarding_completed_at && (
              <span className="badge bg-amber-50 text-amber-700" title="The onboarding wizard has not been completed for this tenant">Onboarding pending</span>
            )}
          </span>
        );
      case 'actions':
        return editing ? (
          <span className="inline-flex gap-1">
            <button onClick={() => saveEdit(t.id)} className="p-1 text-green-600 hover:bg-green-50 rounded"><Check size={14} /></button>
            <button onClick={() => setEditingId(null)} className="p-1 text-gray-400 hover:bg-gray-100 rounded"><X size={14} /></button>
          </span>
        ) : !isAdmin ? <span className="text-gray-300">—</span> : (
          <span className="inline-flex gap-1">
            <button onClick={() => { setSelectedTenantId(t.id); navigate('/onboarding'); }} title="Onboarding wizard"
              className="p-1 text-gray-400 hover:text-primary-600 hover:bg-gray-50 rounded"><ClipboardList size={13} /></button>
            <button onClick={() => startEdit(t)} title="Edit" className="p-1 text-gray-400 hover:text-primary-600 hover:bg-gray-50 rounded"><Pencil size={13} /></button>
            <button onClick={() => toggleActive(t)} title={t.active ? 'Deactivate' : 'Reactivate'}
              className="p-1 text-gray-400 hover:text-amber-600 hover:bg-gray-50 rounded">
              {t.active ? <Ban size={13} /> : <CheckCircle2 size={13} />}
            </button>
          </span>
        );
      default:
        return null;
    }
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-xl font-semibold text-gray-900">Tenants (End customers)</h1>
        {isAdmin && (
<button onClick={() => setShowForm(v => !v)} className="btn-primary flex items-center gap-1">
          <Plus size={16} /> New tenant
        </button>
)}
      </div>

      {showForm && (
        <form onSubmit={handleCreate} className="card p-4 mb-4 flex flex-wrap gap-3 items-end">
          {error && <div className="w-full text-sm text-red-600">{error}</div>}
          {user?.scopeLevel === 'platform' && (
            <div>
              <label className="block text-xs text-gray-500 mb-1">Partner</label>
              <select value={partnerId} onChange={e => setPartnerId(e.target.value)} required className="input-field">
                <option value="">Select…</option>
                {partners.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>
          )}
          <div>
            <label className="block text-xs text-gray-500 mb-1">Name</label>
            <input value={name} onChange={e => setName(e.target.value)} required className="input-field" />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Slug</label>
            <input value={slug} onChange={e => setSlug(e.target.value)} required className="input-field" />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Contact email</label>
            <input type="email" value={contactEmail} onChange={e => setContactEmail(e.target.value)}
              placeholder="security@customer.com" className="input-field" />
          </div>
          <button type="submit" className="btn-primary">Create</button>
        </form>
      )}

      <div className="card overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr>
              {orderedDefs.map(d => (
                <DraggableTh key={d.key} colKey={d.key} onMove={moveColumn} className="table-header">
                  {d.label}
                </DraggableTh>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td className="table-cell text-gray-400" colSpan={6}>Loading…</td></tr>}
            {!loading && tenants.length === 0 && (
              <tr><td className="table-cell text-gray-400" colSpan={6}>No tenant created yet</td></tr>
            )}
            {tenants.map(t => (
              <React.Fragment key={t.id}>
                <tr className="border-t border-gray-100 hover:bg-gray-50">
                  {orderedDefs.map(d => <td key={d.key} className="table-cell">{renderCell(t, d.key)}</td>)}
                </tr>
                {editingId === t.id && (
                  <tr className="bg-gray-50/60">
                    <td className="table-cell" colSpan={orderedDefs.length}>
                      <div className="text-xs text-gray-500 mb-2">Company details for the report cover page ("Prepared for")</div>
                      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                        {COMPANY_FIELDS.map(f => (
                          <div key={f.key}>
                            <label className="block text-[11px] text-gray-400 mb-0.5">{f.label}</label>
                            <input value={editCompany[f.key] || ''} onChange={e => setEditCompany(prev => ({ ...prev, [f.key]: e.target.value }))}
                              className="input-field py-1 text-xs w-full" />
                          </div>
                        ))}
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
